import { computed, ref } from 'vue';
import { i18n } from 'boot/i18n';
import { notifyNegative, notifySuccess } from 'src/composables/interactions';

import { MIN_LEARNING_LENGTH, LEARNING_DECISIONS } from '../../domain/constants/auditLearnings';
import { isUnsafeLearningErrorCode } from '../../domain/constants/auditLearningErrorCodes';
import { AIUnavailableError } from '../../domain/errors/AIUnavailableError';
import { useAuditLearnings } from './useAuditLearnings';

/** @typedef {import('vue').Ref} Ref */
/** @typedef {import('../../domain/entities/AuditLearning.types').FeedbackLearningOutcome} FeedbackLearningOutcome */

function learningFailureMessageKey(err, fallbackKey) {
  if (isUnsafeLearningErrorCode(err?.details?.code)) {
    return 'noteAuditor.feedback.notifyLearningUnsafe';
  }

  return err instanceof AIUnavailableError ? 'common.errors.aiUnavailable' : fallbackKey;
}

function normalizeLearning(value) {
  return value?.trim?.() ?? '';
}

// eslint-disable-next-line import/prefer-default-export
export function useAuditLearningProposal(sessionActivitySource) {
  const sessionActivity = computed(
    () => (typeof sessionActivitySource === 'function'
      ? sessionActivitySource()
      : sessionActivitySource),
  );

  const {
    commitLearning,
    editLearning,
    removeLearning,
    isProcessing,
  } = useAuditLearnings();

  /** @type {Ref<FeedbackLearningOutcome|null>} */
  const proposal = ref(null);
  const editLearningValue = ref('');

  const validators = {
    requiredTrimmed(val) {
      return !!normalizeLearning(val) || i18n.t('common.validationRequired');
    },
    minLengthTrimmed(val) {
      return normalizeLearning(val).length >= MIN_LEARNING_LENGTH
        || i18n.t('common.validationMinLength', { min: MIN_LEARNING_LENGTH });
    },
  };

  const rules = {
    learning: [
      validators.requiredTrimmed,
      validators.minLengthTrimmed,
    ],
  };

  function hasRuleError(value, ruleList = []) {
    return ruleList.some((rule) => rule(value) !== true);
  }

  const normalizedLearning = computed(() => normalizeLearning(editLearningValue.value));

  const hasProposal = computed(() => !!proposal.value);

  const isNewProposal = computed(
    () => proposal.value?.learningDecision === LEARNING_DECISIONS.PROPOSE_NEW,
  );
  const isUpdateProposal = computed(
    () => proposal.value?.learningDecision === LEARNING_DECISIONS.UPDATE_EXISTING,
  );
  const isDeleteProposal = computed(
    () => proposal.value?.learningDecision === LEARNING_DECISIONS.DELETE_EXISTING,
  );

  const hasLearningValidationError = computed(() => {
    if (!isNewProposal.value && !isUpdateProposal.value) {
      return false;
    }

    return hasRuleError(editLearningValue.value, rules.learning);
  });

  const isMatchedLearningValid = computed(() => {
    return !!proposal.value?.matchedLearningId && !!normalizeLearning(proposal.value?.matchedLearning?.content);
  });

  function getCurrentSessionActivity() {
    return sessionActivity.value;
  }

  function getLearningContext(currentSessionActivity) {
    return {
      sessionActivityType: currentSessionActivity.type,
      ...(currentSessionActivity.sessionNoteTemplateId && {
        sessionNoteTemplateId: currentSessionActivity.sessionNoteTemplateId,
      }),
    };
  }

  function setProposal(feedbackLearningOutcome) {
    proposal.value = feedbackLearningOutcome;

    if (feedbackLearningOutcome?.learningDecision === LEARNING_DECISIONS.PROPOSE_NEW) {
      editLearningValue.value = feedbackLearningOutcome.proposedLearning?.content ?? '';
      return;
    }

    if (feedbackLearningOutcome?.learningDecision === LEARNING_DECISIONS.UPDATE_EXISTING) {
      editLearningValue.value = feedbackLearningOutcome.updatedLearning?.content ?? '';
      return;
    }

    editLearningValue.value = '';
  }

  function dismissProposal() {
    proposal.value = null;
    editLearningValue.value = '';
  }

  async function approveNewProposal() {
    const currentSessionActivity = getCurrentSessionActivity();

    if (
      !currentSessionActivity
      || !proposal.value
      || !isNewProposal.value
      || hasLearningValidationError.value
    ) {
      return;
    }

    try {
      const context = getLearningContext(currentSessionActivity);

      await commitLearning(
        currentSessionActivity.clinic,
        normalizedLearning.value,
        context,
      );

      dismissProposal();
      notifySuccess(i18n.t('noteAuditor.feedback.notifyLearningCommitSuccess'));
    } catch (err) {
      console.error('Error approving new learning proposal:', err);
      notifyNegative(i18n.t(learningFailureMessageKey(err, 'noteAuditor.feedback.notifyLearningCommitFail')));
    }
  }

  async function approveUpdateProposal() {
    const currentSessionActivity = getCurrentSessionActivity();

    if (
      !currentSessionActivity
      || !proposal.value
      || !isUpdateProposal.value
      || hasLearningValidationError.value
      || !isMatchedLearningValid.value
    ) {
      return;
    }

    try {
      await editLearning(
        currentSessionActivity.clinic,
        proposal.value.matchedLearningId,
        normalizedLearning.value,
      );

      dismissProposal();
      notifySuccess(i18n.t('noteAuditor.feedback.notifyLearningUpdateSuccess'));
    } catch (err) {
      console.error('Error approving learning update proposal:', err);
      notifyNegative(i18n.t(learningFailureMessageKey(err, 'noteAuditor.feedback.notifyLearningUpdateFail')));
    }
  }

  async function approveDeleteProposal() {
    const currentSessionActivity = getCurrentSessionActivity();

    if (
      !currentSessionActivity
      || !proposal.value
      || !isDeleteProposal.value
      || !isMatchedLearningValid.value
    ) {
      return;
    }

    try {
      await removeLearning(
        currentSessionActivity.clinic,
        proposal.value.matchedLearningId,
      );

      dismissProposal();
      notifySuccess(i18n.t('noteAuditor.feedback.notifyLearningDeleteSuccess'));
    } catch (err) {
      console.error('Error approving learning delete proposal:', err);
      notifyNegative(i18n.t('noteAuditor.feedback.notifyLearningDeleteFail'));
    }
  }

  return {
    isProcessing,
    proposal,
    hasProposal,
    isNewProposal,
    isUpdateProposal,
    isDeleteProposal,
    editLearningValue,
    normalizedLearning,
    rules,
    hasLearningValidationError,
    isMatchedLearningValid,
    setProposal,
    dismissProposal,
    approveNewProposal,
    approveUpdateProposal,
    approveDeleteProposal,
  };
}
